import { useCallback, useSyncExternalStore } from 'react';
import type { ConversationId, MessageId } from '@cyphex/types';
import { draftStore, type LocalDraft } from '../store/draftStore';

const EMPTY: LocalDraft = { text: '', replyToId: null, updatedAt: 0 };

interface UseComposerDraftResult {
  draft: LocalDraft;
  setText: (text: string) => void;
  setReplyTo: (messageId: MessageId | null) => void;
  clear: () => void;
}

/** Per-conversation draft, persisted so switching chats never loses typing. */
export function useComposerDraft(conversationId: ConversationId | null): UseComposerDraftResult {
  const subscribe = useCallback((listener: () => void) => draftStore.subscribe(listener), []);
  const snapshot = useCallback(
    () => (conversationId ? draftStore.select(conversationId) : EMPTY),
    [conversationId],
  );
  const draft = useSyncExternalStore(subscribe, snapshot, snapshot);

  const setText = useCallback(
    (text: string) => {
      if (conversationId) draftStore.set(conversationId, { text });
    },
    [conversationId],
  );

  const setReplyTo = useCallback(
    (messageId: MessageId | null) => {
      if (conversationId) draftStore.set(conversationId, { replyToId: messageId });
    },
    [conversationId],
  );

  const clear = useCallback(() => {
    if (conversationId) draftStore.clear(conversationId);
  }, [conversationId]);

  return { draft, setText, setReplyTo, clear };
}
